<template>
    <div class="bank-reconciliation-main">
        <ReconciliationSummaryCards :statementPayableTotal="statementPayableTotal"
            :statementPayableCount="statementPayableCount" :statementReceivableTotal="statementReceivableTotal"
            :statementReceivableCount="statementReceivableCount" :recordPayableTotal="recordPayableTotal"
            :recordPayableCount="recordPayableCount" :recordReceivableTotal="recordReceivableTotal"
            :recordReceivableCount="recordReceivableCount" />

        <div v-if="loading">
            <span>Aguarde, estamos carregando os dados para conciliação...</span>
            <Loading type="line" :center="false" />
        </div>

        <div v-else>
            <b-row class="no-gutters">
                <b-col sm="6" class="bank-reconciliation-main-column">
                    <BankReconciliationColumnHeader title="Extrato Bancário" icon="fa-solid fa-money-bill-transfer"
                        :count="pendingStatement.length" :total="statementTotal" />
                </b-col>
                <b-col sm="6" class="bank-reconciliation-main-column">
                    <BankReconciliationColumnHeader title="Lançamentos Cadastrados"
                        icon="fa-regular fa-hand-holding-dollar" :count="visibleRecord.length" :total="recordTotal" />
                </b-col>
            </b-row>

            <BankReconciliationPairedList ref="listContainer" :statementItems="statement" :record="record"
                :listHeight="columnScrollHeight" emptyMessage="Nenhum lançamento de extrato encontrado."
                @toggle-select="toggleSelectStatement" @delete="deleteStatementItem" @conciliar="confirmSuggestion"
                @desfazer="undoMatch" @force-reconcile="forceReconcile" @search="openSearchModal"
                @create="openCreateModal" @link-more="openLinkMoreModal" />
        </div>

        <ManualReconcileBar v-if="selectedStatementIds.length > 0" :selectedStatementCount="selectedStatementIds.length"
            @remove-batch="removeSelectedStatementItems" @cancel="cancelManualSelection" />

        <Modal title="Confirmar Conciliação" :width="800" v-if="showModal('manualReconcileConfirm')"
            :onHideModal="closeManualReconcileConfirm">
            <ManualReconcileConfirmModal v-if="confirmStatementItem && confirmRecordItem"
                :statementItem="confirmStatementItem" :recordItem="confirmRecordItem" :statement="statement"
                @confirm="confirmReconciliation" @cancel="closeManualReconcileConfirm" />
        </Modal>

        <Modal title="Pesquisar Lançamento" :width="600" :height="300" v-if="showModal('searchRecord')"
            :onHideModal="closeSearchModal">
            <RecordSearchModal v-if="searchModalItem" :statementItem="searchModalItem" :filter="recordFilter"
                @select="linkFromSearch" @cancel="closeSearchModal" @open-filter="openRecordFilterModal" />
        </Modal>

        <Modal title="Filtro" :width="500" :height="400" :closeButton="false" :closeOnEsc="false"
            v-if="showModal('recordSearchFilter')">
            <RecordSearchFilterModal :filter="recordFilter" @apply="applyRecordFilter" @cancel="cancelRecordFilter"
                @clear="clearRecordFilter" />
        </Modal>

        <Modal title="Vincular mais um item do extrato" :width="700" :height="650" v-if="showModal('linkMoreStatement')"
            :onHideModal="closeLinkMoreModal">
            <RecordLinkMoreModal v-if="linkMoreRecordItem" :recordItem="linkMoreRecordItem" :statement="statement"
                @select="onLinkMoreSelect" @cancel="closeLinkMoreModal" />
        </Modal>

        <Modal title="Importar Extrato" :width="550" v-if="showModal('importExtrato')">
            <ImportModal />
        </Modal>

        <Modal title="Criar Lançamento a partir do Extrato" :width="1200" :height="700"
            v-if="showModal('createFromStatement')">
            <CreateFromStatementModal v-if="createModalItem" :statementItem="createModalItem" />
        </Modal>
    </div>
</template>
<script>

import Loading from "@/ui/shared/Loading.vue";
import Modal from "@/ui/forms/Modal";

import ReconciliationSummaryCards from './ReconciliationSummaryCards.vue'
import BankReconciliationColumnHeader from './BankReconciliationColumnHeader.vue'
import BankReconciliationPairedList from './BankReconciliationPairedList.vue'
import ManualReconcileBar from './ManualReconcileBar.vue'
import ManualReconcileConfirmModal from './ManualReconcileConfirmModal.vue'
import ImportModal from './ImportModal.vue'
import CreateFromStatementModal from './CreateFromStatementModal.vue'
import RecordSearchModal from './RecordSearchModal.vue'
import RecordSearchFilterModal from './RecordSearchFilterModal.vue'
import RecordLinkMoreModal from './RecordLinkMoreModal.vue'

import BankReconciliationService from "../../services/bankReconciliation.service.js";
import PayableReceivableService from "../../services/payableReceivable.service.js";
import { withinBalance, VALUE_TOLERANCE } from "../../mixins/reconciliationRecord.mixin.js";

import { mapActions, mapMutations, mapState, mapGetters } from "vuex";

const DEFAULT_TOP_OFFSET = 370;
const MIN_LIST_HEIGHT = 380;
const BOTTOM_MARGIN = 4;
const SELECTION_BAR_OFFSET = 90;

export default {
    name: "BankReconciliationMain",
    components: {
        Loading,
        Modal,
        ReconciliationSummaryCards,
        BankReconciliationColumnHeader,
        BankReconciliationPairedList,
        ManualReconcileBar,
        ManualReconcileConfirmModal,
        ImportModal,
        CreateFromStatementModal,
        RecordSearchModal,
        RecordSearchFilterModal,
        RecordLinkMoreModal,
    },
    data() {
        return {
            loading: true,
            statement: [],
            record: [],
            totals: {},
            selectedStatementIds: [],
            confirmStatementItem: null,
            confirmRecordItem: null,
            createModalItem: null,
            searchModalItem: null,
            recordFilter: { period: [], valueStart: 0, valueEnd: 0 },
            linkMoreRecordItem: null,
            columnScrollHeight: 600,
        };
    },
    mounted() {
        this.updateColumnScrollHeight();
        this.loadAll();
        window.addEventListener("resize", this.updateColumnScrollHeight);
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.updateColumnScrollHeight);
    },
    computed: {
        ...mapState("generic", ["event"]),
        ...mapGetters("generic", ["showModal"]),
        statementPayableTotal() {
            return this.totals.statementPayableTotal || 0;
        },
        statementPayableCount() {
            return this.totals.statementPayableCount || 0;
        },
        statementReceivableTotal() {
            return this.totals.statementReceivableTotal || 0;
        },
        statementReceivableCount() {
            return this.totals.statementReceivableCount || 0;
        },
        recordPayableTotal() {
            return this.totals.recordPayableTotal || 0;
        },
        recordPayableCount() {
            return this.totals.recordPayableCount || 0;
        },
        recordReceivableTotal() {
            return this.totals.recordReceivableTotal || 0;
        },
        recordReceivableCount() {
            return this.totals.recordReceivableCount || 0;
        },
        statementTotal() {
            return this.totals.statementTotal || 0;
        },
        pendingStatement() {
            return this.statement.filter((item) => item.status != 'conciliado');
        },
        visibleRecord() {
            return this.record.filter((item) => item.pairIds.length > 0);
        },
        recordTotal() {
            return this.visibleRecord.reduce((accumulator, item) => accumulator + item.value, 0);
        },
    },
    methods: {
        ...mapActions("generic", ["getApi", "postApi"]),
        ...mapMutations("generic", ["openModal", "hideModal", "removeLoading", "addNotifications"]),
        async loadAll() {
            this.loading = true;

            try {
                let response = await this.getApi({ url: BankReconciliationService.urlGetAll });
                let content = (response && response.content) || {};

                this.statement = (content.statement || []).map((item) => ({
                    ...item,
                    bankAccount: item.bankAccount && item.bankAccount.id
                        ? { id: item.bankAccount.id, content: item.bankAccount.name }
                        : {},
                    matchedSuggestion: item.status === 'sugerido',
                    selected: false,
                }));

                this.record = (content.record || []).map((item) => ({
                    ...item,
                    pairIds: item.pairIds || [],
                    pairedTotal: item.pairedTotal || 0,
                    baseRemainingValue: item.baseRemainingValue != null ? Math.abs(item.baseRemainingValue) : Math.abs(item.value),
                    remainingValue: item.remainingValue != null ? Math.abs(item.remainingValue) : Math.abs(item.value),
                    status: item.status || 'pendente',
                }));

                this.totals = content.totals || {};
            } catch (error) {
                console.error("Falha ao carregar dados de conciliação bancária:", error);
            } finally {
                this.loading = false;
                this.$nextTick(() => this.updateColumnScrollHeight());
            }
        },
        calculateColumnScrollHeight() {
            let listContainer = this.$refs.listContainer;
            let element = listContainer && listContainer.$el ? listContainer.$el : null;
            let topOffset = element ? element.getBoundingClientRect().top : DEFAULT_TOP_OFFSET;
            let bottomOffset = this.selectedStatementIds.length > 0 ? SELECTION_BAR_OFFSET : BOTTOM_MARGIN;
            let availableHeight = window.innerHeight - topOffset - bottomOffset;
            return Math.max(Math.floor(availableHeight), MIN_LIST_HEIGHT);
        },
        updateColumnScrollHeight() {
            this.columnScrollHeight = this.calculateColumnScrollHeight();
        },
        pairedStatementItems(recordItem) {
            return this.statement.filter((statementItem) => recordItem.pairIds.includes(statementItem.id));
        },
        updateRecordAggregate(recordItem) {
            let pairedTotal = this.pairedStatementItems(recordItem)
                .reduce((accumulator, statementItem) => accumulator + Math.abs(statementItem.value), 0);
            let remaining = Math.max(recordItem.baseRemainingValue - pairedTotal, 0);

            recordItem.pairedTotal = pairedTotal;
            recordItem.remainingValue = remaining;

            if (recordItem.pairIds.length == 0) recordItem.status = 'pendente';
            else if (remaining > VALUE_TOLERANCE) recordItem.status = 'parcial';
            else recordItem.status = 'completo';
        },
        toggleSelectStatement(item) {
            if (item.status == 'conciliado') return;
            let index = this.selectedStatementIds.indexOf(item.id);
            if (index == -1) this.selectedStatementIds.push(item.id);
            else this.selectedStatementIds.splice(index, 1);

            this.statement.forEach((statementItem) => {
                statementItem.selected = this.selectedStatementIds.includes(statementItem.id);
            });
        },
        cancelManualSelection() {
            this.selectedStatementIds = [];
            this.statement.forEach((item) => { item.selected = false; });
            this.removeLoading(["btnManualCancel"]);
        },
        openConfirmModal(statementItem, recordItem) {
            this.confirmStatementItem = statementItem;
            this.confirmRecordItem = recordItem;
            this.openModal("manualReconcileConfirm");
        },
        undoMatch(statementItem) {
            let recordItem = this.record.find((item) => item.id == statementItem.pairId);
            statementItem.status = 'pendente';
            statementItem.pairId = null;
            statementItem.matchedSuggestion = null;
            statementItem.confidenceText = null;
            statementItem.diffText = null;
            this.removeLoading(["btnDesfazer" + statementItem.id]);
            if (recordItem) {
                recordItem.pairIds = recordItem.pairIds.filter((id) => id != statementItem.id);
                this.updateRecordAggregate(recordItem);
            }
        },
        pairStatementWithRecord(statementItem, recordItem) {
            if (!withinBalance(Math.abs(statementItem.value), recordItem.remainingValue)) {
                this.addNotifications([{ message: "O valor do item do extrato é maior que o saldo restante do lançamento selecionado." }]);
                return false;
            }

            let equal = Math.abs(Math.abs(statementItem.value) - recordItem.remainingValue).toFixed(2) == '0.00';

            statementItem.pairId = recordItem.id;

            if (!equal) {
                statementItem.status = 'divergente';
                statementItem.diffText = 'Diferença de ' + this.$options.filters.currency(Math.abs(Math.abs(statementItem.value) - recordItem.remainingValue)) + ' em relação ao saldo do lançamento cadastrado';
            } else {
                statementItem.status = 'sugerido';
                statementItem.diffText = null;
            }

            if (!recordItem.pairIds.includes(statementItem.id)) recordItem.pairIds.push(statementItem.id);
            this.updateRecordAggregate(recordItem);

            return equal;
        },
        openSearchModal(statementItem) {
            this.searchModalItem = statementItem;
            this.openModal("searchRecord");
        },
        closeSearchModal() {
            this.hideModal();
            this.searchModalItem = null;
            this.recordFilter = { period: [], valueStart: 0, valueEnd: 0 };
            this.removeLoading(["btnCancelRecordSearch"]);
        },
        openRecordFilterModal() {
            this.openModal("recordSearchFilter");
            this.removeLoading(["btnOpenRecordFilter"]);
        },
        applyRecordFilter(filter) {
            this.recordFilter = filter;
            this.openModal("searchRecord");
            this.removeLoading(["btnApplyRecordFilter"]);
        },
        clearRecordFilter(filter) {
            this.recordFilter = filter;
        },
        cancelRecordFilter() {
            this.openModal("searchRecord");
            this.removeLoading(["btnCancelRecordFilter"]);
        },
        linkFromSearch(recordItem) {
            let statementItem = this.searchModalItem;
            this.closeSearchModal();
            this.removeLoading(["btnLink" + recordItem.id]);
            if (!statementItem) return;

            let localRecordItem = this.record.find((item) => item.id == recordItem.id);
            if (!localRecordItem) {
                localRecordItem = {
                    ...recordItem,
                    pairIds: [],
                    pairedTotal: 0,
                    baseRemainingValue: recordItem.remainingValue,
                    remainingValue: recordItem.remainingValue,
                    status: 'pendente',
                };
                this.record.push(localRecordItem);
            } else {
                localRecordItem.chartOfAccounts = recordItem.chartOfAccounts;
                localRecordItem.typePayment = recordItem.typePayment;
                localRecordItem.bankAccount = recordItem.bankAccount;
                localRecordItem.rent = recordItem.rent;
                localRecordItem.baseRemainingValue = recordItem.remainingValue;
                localRecordItem.remainingValue = recordItem.remainingValue;
            }

            this.pairStatementWithRecord(statementItem, localRecordItem);
        },
        openLinkMoreModal(recordItem) {
            this.linkMoreRecordItem = recordItem;
            this.openModal("linkMoreStatement");
            this.removeLoading(["btnLinkMore" + recordItem.id]);
        },
        closeLinkMoreModal() {
            this.hideModal();
            this.linkMoreRecordItem = null;
            this.removeLoading(["btnCancelLinkMore"]);
        },
        onLinkMoreSelect(statementItems) {
            let recordItem = this.linkMoreRecordItem;
            this.closeLinkMoreModal();
            this.removeLoading(["btnConfirmLinkMore"]);
            if (recordItem) {
                statementItems.forEach((statementItem) => this.pairStatementWithRecord(statementItem, recordItem));
            }
        },
        closeManualReconcileConfirm() {
            this.hideModal();
            this.cancelManualSelection();
            this.confirmStatementItem = null;
            this.confirmRecordItem = null;
            this.removeLoading(["btnConfirmManualReconcile", "btnCancelManualReconcile"]);
        },
        async confirmReconciliation(fields) {
            let recordItem = this.confirmRecordItem;
            if (!recordItem) {
                this.removeLoading(["btnConfirmManualReconcile"]);
                return;
            }

            let groupItems = this.pairedStatementItems(recordItem).filter((item) => item.status != 'conciliado');
            let remaining = recordItem.baseRemainingValue;
            let completingIds = [];
            let reconciledIds = [];
            let hasError = false;

            for (let statementItem of groupItems) {
                let itemValue = Math.abs(statementItem.value);
                let completesRecord = Math.abs(itemValue - remaining).toFixed(2) == '0.00';

                if (completesRecord) {
                    completingIds.push(statementItem.id);
                    remaining = 0;
                } else {
                    let paid = await this.createRecordPartialPayment(statementItem, recordItem, fields);
                    if (paid) {
                        this.markStatementItemAsReconciled(statementItem);
                        reconciledIds.push(statementItem.id);
                    } else {
                        hasError = true;
                    }
                    remaining = Math.max(remaining - itemValue, 0);
                }
            }

            if (completingIds.length) {
                let confirmed = await this.executeConfirm(completingIds, recordItem, fields);
                if (confirmed) {
                    groupItems
                        .filter((statementItem) => completingIds.includes(statementItem.id))
                        .forEach((statementItem) => this.markStatementItemAsReconciled(statementItem));
                    reconciledIds.push(...completingIds);
                } else {
                    hasError = true;
                }
            }

            if (reconciledIds.length) {
                this.updateRecordAggregate(recordItem);
                await this.deleteReconciledStatementItems(reconciledIds);
            }

            this.removeLoading(["btnConfirmManualReconcile"]);
            if (!hasError) this.closeManualReconcileConfirm();
        },
        markStatementItemAsReconciled(statementItem) {
            if (statementItem.status == 'conciliado') return;
            statementItem.status = 'conciliado';
            statementItem.selected = false;
            this.selectedStatementIds = this.selectedStatementIds.filter((id) => id != statementItem.id);
            this.discountStatementTotals(statementItem);
        },
        subtractFromTotal(total, value) {
            let amount = Math.abs(value);
            return total >= 0 ? total - amount : total + amount;
        },
        discountStatementTotals(statementItem) {
            let isPayable = statementItem.type == 1;
            let totals = { ...this.totals };

            totals.statementTotal = this.subtractFromTotal(totals.statementTotal || 0, statementItem.value);

            if (isPayable) {
                totals.statementPayableTotal = this.subtractFromTotal(totals.statementPayableTotal || 0, statementItem.value);
                totals.statementPayableCount = Math.max((totals.statementPayableCount || 0) - 1, 0);
            } else {
                totals.statementReceivableTotal = this.subtractFromTotal(totals.statementReceivableTotal || 0, statementItem.value);
                totals.statementReceivableCount = Math.max((totals.statementReceivableCount || 0) - 1, 0);
            }

            this.totals = totals;
        },
        async deleteReconciledStatementItems(ids) {
            let params = { url: BankReconciliationService.urlDelete, obj: { ids } };
            await this.postApi(params);
        },
        async createRecordPartialPayment(statementItem, recordItem, fields) {
            let params = {
                url: PayableReceivableService.urlPartialPaymentCreate,
                obj: {
                    payableReceivableId: recordItem.id,
                    value: Math.abs(statementItem.value),
                    conciliationDate: statementItem.dueDate,
                    chartOfAccounts: fields.chartOfAccounts,
                    typePayment: fields.typePayment,
                    bankAccount: fields.bankAccount,
                },
            };
            let response = await this.postApi(params);
            if (!response || !response.success) {
                console.error("Falha ao lançar baixa parcial da conciliação bancária:", response && response.notifications);
                return false;
            }
            return true;
        },
        openConfirmModalFromButton(statementItem, buttonKeyPrefix) {
            let recordItem = this.record.find((item) => item.id == statementItem.pairId);
            this.removeLoading([buttonKeyPrefix + statementItem.id]);
            if (recordItem) this.openConfirmModal(statementItem, recordItem);
        },
        confirmSuggestion(statementItem) {
            this.openConfirmModalFromButton(statementItem, "btnConciliar");
        },
        forceReconcile(statementItem) {
            this.openConfirmModalFromButton(statementItem, "btnForceReconcile");
        },
        async executeConfirm(statementIds, recordItem, fields) {
            let params = {
                url: BankReconciliationService.urlConfirm,
                obj: {
                    payableReceivableId: recordItem.id,
                    statementIds,
                    chartOfAccounts: fields.chartOfAccounts,
                    typePayment: fields.typePayment,
                    bankAccount: fields.bankAccount,
                },
            };
            let response = await this.postApi(params);
            return !!(response && response.success);
        },
        async deleteStatementItem(statementItem) {
            let params = { url: BankReconciliationService.urlDelete, obj: { ids: [statementItem.id] } };
            let response = await this.postApi(params);
            if (response && response.success) {
                let recordItem = this.record.find((item) => item.id == statementItem.pairId);
                if (recordItem) {
                    recordItem.pairIds = recordItem.pairIds.filter((id) => id != statementItem.id);
                    this.updateRecordAggregate(recordItem);
                }
                this.statement = this.statement.filter((item) => item.id != statementItem.id);
                this.selectedStatementIds = this.selectedStatementIds.filter((id) => id != statementItem.id);
            }
        },
        openCreateModal(statementItem) {
            this.createModalItem = statementItem;
            this.openModal("createFromStatement");
            this.removeLoading(["btnCreate" + statementItem.id]);
        },
        async removeSelectedStatementItems() {
            let ids = this.selectedStatementIds.slice();
            this.removeLoading(["btnRemoveBatch"]);
            if (!ids.length) return;

            let params = { url: BankReconciliationService.urlDelete, obj: { ids } };
            let response = await this.postApi(params);
            if (response && response.success) {
                this.record.forEach((recordItem) => {
                    if (!recordItem.pairIds.length) return;
                    let filtered = recordItem.pairIds.filter((id) => !ids.includes(id));
                    if (filtered.length != recordItem.pairIds.length) {
                        recordItem.pairIds = filtered;
                        this.updateRecordAggregate(recordItem);
                    }
                });
                this.statement = this.statement.filter((item) => !ids.includes(item.id));
                this.cancelManualSelection();
            }
        },
    },
    watch: {
        event: {
            handler(event) {
                if (event.name == "fileLoaded" || event.name == "importPluggy") {
                    this.loadAll();
                }

                if (event.name == "createFromStatementExecuted") {
                    let statementItem = this.statement.find((item) => item.id == event.data.statementId);
                    if (statementItem) this.markStatementItemAsReconciled(statementItem);
                }
            },
            deep: true,
        },
        selectedStatementIds() {
            this.$nextTick(() => {
                this.updateColumnScrollHeight();
            });
        },
    },
};
</script>

<style scoped>
.bank-reconciliation-main {
    width: 100%;
    max-width: 100%;
    overflow: hidden;
}

.bank-reconciliation-main-column {
    padding: 0 8px;
    min-width: 0;
}
</style>
